import { useEffect, useMemo, useState } from 'react';

import {
  groupDesktopTargetsForSelector,
  resolveDesktopTargetSelection,
  useDesktopTargetRoster,
  useNativeInstanceId,
  useResidentSetup,
  useWorkflow,
  type DesktopTarget,
  type DesktopTargetSticky,
  type ResidentSetup,
  type ResolvedDesktopTargetSelection,
} from '@heidi/agents/data';
import { useDesktopTargetViewSelection } from '../../work-home/use-desktop-target-view-selection';

export type DesktopTargetSelectionSurface = 'landing' | 'project' | 'overlay';

export type DesktopTargetRosterStatus = 'loading' | 'failed' | 'ready';

export type DesktopTargetSelectionState = {
  visible: boolean;
  rosterStatus: DesktopTargetRosterStatus;
  selection: ResolvedDesktopTargetSelection;
  thisComputer: DesktopTarget | undefined;
  live: DesktopTarget[];
  dead: DesktopTarget[];
  /** Present when the CUA Cloud user has no Resident yet: inline provision. */
  residentSetup: ResidentSetup | undefined;
  setSticky: (next: DesktopTargetSticky) => void;
  retry: () => void;
};

/** Mirrors the backend: the routine's preferred computer applies while it uses the computer. */
const useRoutinePreferredTargetId = (
  workflowId: string | null | undefined
): string | null => {
  const { data: workflow } = useWorkflow(workflowId ?? undefined);
  if (!workflowId || workflow?.workflow_id !== workflowId) return null;
  return workflow.uses_computer ? (workflow.preferred_target_id ?? null) : null;
};

export const useDesktopTargetSelection = ({
  surface,
  visibleOnProject = false,
}: {
  surface: DesktopTargetSelectionSurface;
  visibleOnProject?: boolean;
}): DesktopTargetSelectionState => {
  const visible = surface === 'landing' || surface === 'overlay' || visibleOnProject;
  const rosterQuery = useDesktopTargetRoster(visible);
  const residentSetup = useResidentSetup(visible);
  const viewSelection = useDesktopTargetViewSelection();
  const [localOverride, setLocalOverride] = useState<DesktopTargetSticky>(null);
  const routinePreferredId = useRoutinePreferredTargetId(
    visible ? viewSelection?.attachedRoutineId : null
  );
  // With no pick, show the computer the backend will choose for the attached routine.
  const sticky = viewSelection?.override ?? routinePreferredId ?? localOverride;
  const setSticky = viewSelection?.setOverride ?? setLocalOverride;
  const [rosterFailureLatched, setRosterFailureLatched] = useState(false);
  const nativeIdentity = useNativeInstanceId(visible);
  const instanceId = nativeIdentity.data ?? null;
  // Stale-while-error: a failed refetch with a cached roster keeps serving the
  // cached targets (background retries continue). Only a failure with no data
  // at all degrades the picker — otherwise one transient timeout (e.g. the
  // browser's per-host connection cap under many SSE streams) would blank the
  // list and drop the user's selection mid-send.
  const hasRoster = rosterQuery.data !== undefined;
  const rosterFailed =
    !hasRoster && (rosterQuery.failureCount > 0 || rosterFailureLatched);

  useEffect(() => {
    if (rosterQuery.failureCount > 0) {
      setRosterFailureLatched(true);
    } else if (rosterQuery.isSuccess && !rosterQuery.isFetching) {
      setRosterFailureLatched(false);
    }
  }, [rosterQuery.failureCount, rosterQuery.isFetching, rosterQuery.isSuccess]);

  const rosterStatus: DesktopTargetRosterStatus = hasRoster
    ? 'ready'
    : rosterFailed
      ? 'failed'
      : 'loading';

  const groups = useMemo(
    () => groupDesktopTargetsForSelector(rosterQuery.data?.targets ?? [], instanceId),
    [instanceId, rosterQuery.data]
  );

  return {
    visible,
    rosterStatus,
    selection: resolveDesktopTargetSelection({
      sticky,
      roster: rosterQuery.data,
      instanceId,
    }),
    thisComputer: groups.thisComputer,
    live: groups.live,
    dead: groups.dead,
    residentSetup,
    setSticky,
    retry: () => {
      void rosterQuery.refetch();
    },
  };
};
